// frontend/src/components/VerificationModal/VerificationModal.jsx

import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { IconButton, Button, CircularProgress, Tooltip, Alert, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import './VerificationModal.css';

import LicenseModal from '../AdCreativeTools/LicenseModal';
import { AuthContext } from '../../contexts/AuthContext';

const VerificationModal = ({ 
    isOpen, 
    onClose, 
    verificationUrl, 
    qrCodeImageUrl, 
    isGuest,
    exactHash, 
    contentType,
    contextHash
}) => {
  const { t } = useTranslation();
  const { user, fetchWithAuth } = useContext(AuthContext);
  
  const [copied, setCopied] = useState(false);
  const [isQrLoading, setIsQrLoading] = useState(true);
  const [isQrError, setIsQrError] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [isDownloadingQr, setIsDownloadingQr] = useState(false);

  // --- LICENSE STATE ---
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [isCheckingLicense, setIsCheckingLicense] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  
  // Pricing State
  const [pricingConfig, setPricingConfig] = useState(null);
  const [currencyRates, setCurrencyRates] = useState({ USD: 1 });
   const preferredCurrency = user?.preferred_currency || 'USD';

  const fullVerificationUrl = `${window.location.origin}${verificationUrl}`;

  // --- FETCH LICENSE & CONFIG ON OPEN ---
  useEffect(() => {
    if (isOpen && !isGuest && exactHash) {
        setIsCheckingLicense(true);
        setIsQrLoading(true); 

        const loadData = async () => {
            try {
                // Append context hash to the API call if it exists
                let url = `/api/verify/all/${exactHash}`;
                if (contextHash) {
                    url += `?ctx=${contextHash}`;
                }
                const [licenseRes, configRes, ratesRes] = await Promise.all([
                    fetchWithAuth(url), // Use the dynamic URL
                    fetchWithAuth('/api/config/billing'),
                    fetchWithAuth('/api/user/billing?limit=1')
                ]);

                if (licenseRes.ok) {
                    const data = await licenseRes.json();
                    const status = data.license_info;
                    if (status && status.status === 'inactive' && !status.expires_at) {
                        setLicenseStatus(null); 
                    } else {
                        setLicenseStatus(status);
                    }
                }

                if (configRes.ok) setPricingConfig(await configRes.json());
                if (ratesRes.ok) {
                    const rateData = await ratesRes.json();
                    if (rateData.currency_rates) setCurrencyRates(rateData.currency_rates);
                }

            } catch (e) {
                console.error("Failed to load verification modal data:", e);
            } finally {
                setIsCheckingLicense(false);
            }
        };
        loadData();
    } else if (isOpen) {
        setIsQrLoading(true);
        setIsQrError(false);
    }
  }, [isOpen, isGuest, exactHash, fetchWithAuth, contextHash]);


  const handleCopyLink = () => {
    if (isGuest) return;
    navigator.clipboard.writeText(fullVerificationUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleDownloadQr = async () => {
      if (!qrCodeImageUrl || isDownloadingQr) return;
      
      setIsDownloadingQr(true); 
      try {
          // Fetch the image blob
          const response = await fetch(qrCodeImageUrl);
          const blob = await response.blob();
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          // Extract short hash for filename or fallback
          const shortHash = exactHash ? exactHash.substring(0, 8) : 'code';
          a.download = `verification-qr-${shortHash}.png`;
          
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          // Cleanup
          setTimeout(() => {
              window.URL.revokeObjectURL(url);
          }, 60000); 

      } catch (err) {
          console.error("QR Download failed", err);
      } finally {
          setIsDownloadingQr(false); 
      }
  };

const handleLicensePayment = async (durationDays, costUnits) => {
      setIsPaying(true); 
      try {
          const response = await fetchWithAuth('/api/license/activate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  exact_hash: exactHash,
                  content_type: contentType,
                  duration_days: durationDays,
                  context_hash: contextHash
              })
          });
          
          if (!response.ok) throw new Error("Activation failed");

          // 2. SUCCESS: Update Local State to match Backend Logic
          
          const isExtension = licenseStatus && licenseStatus.status === 'active' && licenseStatus.expires_at;
          
          let targetDate;

          if (isExtension) {
             // --- EXTENSION LOGIC ---
             // Parse existing YYYY-MM-DD directly from UTC string to avoid timezone shifts
             const parts = licenseStatus.expires_at.split('T')[0].split('-');
             const year = parseInt(parts[0]);
             const month = parseInt(parts[1]) - 1; // JS months are 0-11
             const day = parseInt(parts[2]);
             
             // Create UTC date and add full duration
             targetDate = new Date(Date.UTC(year, month, day));
             targetDate.setUTCDate(targetDate.getUTCDate() + durationDays);
          } else {
             // --- NEW ACTIVATION LOGIC ---
             // Backend Logic: End Date = Today + (Duration - 1)
             // Example: 1 Day License bought on Jan 15 expires Jan 15.
             targetDate = new Date(); // Local "Today"
             const daysToAdd = Math.max(0, durationDays - 1);
             targetDate.setDate(targetDate.getDate() + daysToAdd);
          }

          // 3. Construct ISO String (YYYY-MM-DD...)
          // We extract Y/M/D based on how we constructed targetDate
          let y, m, d;
          
          if (isExtension) {
              y = targetDate.getUTCFullYear();
              m = targetDate.getUTCMonth() + 1;
              d = targetDate.getUTCDate();
          } else {
              y = targetDate.getFullYear();
              m = targetDate.getMonth() + 1;
              d = targetDate.getDate();
          }

          const mStr = String(m).padStart(2, '0');
          const dStr = String(d).padStart(2, '0');
          // Force time to end of day to match backend/DB format
          const isoString = `${y}-${mStr}-${dStr}T23:59:59.999Z`;

          // 4. Update State
          setLicenseStatus({ 
              status: 'active', 
              expires_at: isoString 
          });

          setIsLicenseModalOpen(false);
          // --- Trigger Download Automatically ---
          handleDownloadQr();
          
      } catch (e) {
          console.error("Payment failed", e);
      } finally {
          setIsPaying(false);
      }
  };


  if (!isOpen) return null;

  // --- SAFE DATE FORMATTER ---
  // --- SAFE EXPIRY FORMATTER ---
  // Specific formatter for License Expiration to prevent rounding up to next day
  const formatExpiryDate = (isoString, language) => {
      if (!isoString) return '';
      try {
          // 1. Isolate YYYY-MM-DD (Ignore time/microseconds)
          const datePart = isoString.split('T')[0];
          if (!datePart) return '';

          // 2. Parse components
          const [year, month, day] = datePart.split('-').map(Number);

          // 3. Create Date in UTC
          const date = new Date(Date.UTC(year, month - 1, day));

          // 4. Format in UTC to prevent local timezone shifts
          return date.toLocaleDateString(language, { 
              year: 'numeric', 
              month: 'short', 
              day: 'numeric',
              timeZone: 'UTC' 
          });
      } catch (e) {
          return '';
      }
  };

  const isLocked = !isGuest && licenseStatus && licenseStatus.status !== 'active';
  const showActivateButton = isLocked || (licenseStatus === null && !isGuest); 
  const isActive = !isGuest && licenseStatus && licenseStatus.status === 'active';

  // Use the helper
  const expiryDateStr = isActive && licenseStatus.expires_at 
      ? formatExpiryDate(licenseStatus.expires_at)
      : '';

  const modalContent = (
    <>
      <div
        className={`verification-modal-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div className="verification-modal-content" onClick={(e) => e.stopPropagation()}>
          <header className="verification-modal-header">
            <h2 id="verification-modal-title">{t('verificationModal.title')}</h2>
            <IconButton onClick={onClose} className="verification-modal-close-button">
              <CloseIcon />
            </IconButton>
          </header>

          <div className="verification-modal-body">
            <p className="verification-modal-description">
              {t('verificationModal.description')}
            </p>

            {/* --- 1. SHOW LOCKED WARNING --- */}
            {showActivateButton && !isCheckingLicense && (
                <Alert severity="warning" sx={{ mb: 2, width: '100%', alignItems: 'center' }}>
                    <Trans i18nKey="adCreativeTools.disclaimers.qr">
                        This asset requires a <strong>Content License</strong>.
                    </Trans>
                </Alert>
            )}

            {/* --- 2. SHOW ACTIVE STATUS + EXTEND BUTTON --- */}
            {isActive && !isCheckingLicense && (
                <Alert 
                    severity="success" 
                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                    sx={{ mb: 2, width: '100%', alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>{t('verificationModal.licenseActiveUntil', { date: expiryDateStr })}</span>
                        <Button 
                            size="small" 
                            color="success" 
                            variant="outlined" 
                            onClick={() => setIsLicenseModalOpen(true)}
                            sx={{ 
                                ml: 2, 
                                whiteSpace: 'nowrap', 
                                minWidth: 'auto',
                                // FORCE HOVER STYLES
                                '&:hover': {
                                    backgroundColor: 'rgba(46, 125, 50, 0.1)', // Light green background
                                    borderColor: '#1b5e20', // Darker green border
                                    color: '#1b5e20' // Darker green text
                                }
                            }}
                        >
                            {t('verificationModal.extendButton')}
                        </Button>
                    </Box>
                </Alert>
            )}

            <div className="qr-code-container" style={{ position: 'relative' }}>
              
              {(isQrLoading || isCheckingLicense) && !isQrError && (
                <div className="qr-spinner-overlay" style={{ flexDirection: 'column', gap: '12px' }}>
                  <CircularProgress />
                  {isCheckingLicense && (
                      <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 500 }}>
                          {t('licenseModal.loading')}
                      </Typography>
                  )}
                </div>
              )}

              {!isCheckingLicense && showActivateButton && (
                  <div style={{
                      position: 'absolute',
                      top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: 'rgba(255,255,255,0.15)', 
                      zIndex: 5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '12px',
                      pointerEvents: 'none' 
                  }}>
                      <div style={{ pointerEvents: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <LockOutlinedIcon sx={{ fontSize: 40, color: '#d32f2f', mb: 1, filter: 'drop-shadow(0 2px 4px rgba(255,255,255,0.8))' }} />
                          <Button 
                              variant="contained" 
                              color="secondary" 
                              size="small"
                              onClick={() => setIsLicenseModalOpen(true)}
                              sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                          >
                              {t('licenseModal.buttons.activate')}
                          </Button>
                      </div>
                  </div>
              )}

              {isQrError ? (
                  <div className="qr-error-message" style={{ color: 'red', padding: '20px' }}>
                      {t('verificationModal.extendButton')}
                  </div>
              ) : (
                <Tooltip title={isLocked ? "Activate license to scan" : t('verificationModal.qrTooltip')} arrow placement="top">
                    <Box
                        component={isLocked ? 'div' : 'a'}
                        {...(!isLocked ? { 
                            href: verificationUrl, 
                            target: "_blank", 
                            rel: "noopener noreferrer" 
                        } : {})}
                        className="qr-code-link"
                        sx={{ 
                            pointerEvents: isLocked ? 'none' : 'auto',
                            display: 'block' 
                        }}
                    >
                        <img
                            src={qrCodeImageUrl}
                            alt="Verification QR Code"
                            className="qr-code-image"
                            onLoad={() => setIsQrLoading(false)}
                            onError={() => { setIsQrLoading(false); setIsQrError(true); }}
                            style={{ visibility: (isQrLoading || isCheckingLicense) ? 'hidden' : 'visible' }}
                        />
                    </Box>
                </Tooltip>
              )}
            </div>

            <div className="copy-link-container" style={{ flexDirection: 'row', gap: '12px', width: '100%', justifyContent: 'center' }}>

              {/* Copy Link Button */}
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyLink}
                className="copy-link-button"
                sx={{ flex: 1 }} // Make buttons share width
              >
                {t('verificationModal.copyButton')}
              </Button>

              {/* Download Button */}
              <Button
                variant="outlined"
                // Import FileDownloadIcon from @mui/icons-material
                startIcon={isDownloadingQr ? <CircularProgress size={20} color="inherit"/> : <FileDownloadIcon />}
                onClick={handleDownloadQr}
                className="copy-link-button" // Reuse class for styling
                disabled={isDownloadingQr}
                sx={{ flex: 1 }}
              >
                {isDownloadingQr ? t('licenseModal.buttons.processing') : t('verificationPage.share.downloadButton')}
              </Button>
            </div>
            {copied && <span className="copy-feedback-modal" style={{ marginTop: '10px' }}>{t('verificationModal.copiedFeedback')}</span>}
          </div>
        </div>
      </div>

      <LicenseModal 
          isOpen={isLicenseModalOpen}
          onClose={() => setIsLicenseModalOpen(false)}
          onConfirm={handleLicensePayment}
          onDownload={() => {
            setIsLicenseModalOpen(false);
            handleDownloadQr();
          }}
          isPaying={isPaying} 
          isLoading={false}
          currentLicense={licenseStatus}
          contentType={contentType}
          pricingConfig={pricingConfig}
          currencyRates={currencyRates}
          selectedCurrency={preferredCurrency}
          mode="activate"
      />
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default VerificationModal;